import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import Modal from "react-bootstrap/Modal";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import * as R from "ramda";
import {
  Check2,
  ChevronLeft,
  ChevronRight,
  ExclamationTriangle,
  Lightbulb,
} from "react-bootstrap-icons";

import { ModalSpecsheets, PICKABLE_FIELDS } from "../modal/ModalSpecsheets";
import { specsheetHandleForm } from "../specsheet/specsheet.redux";
import {
  idx_data_insert_sizes,
  idx_data_insert_types,
  idx_data_pitch,
  idx_data_pitch_16p,
  idx_data_pitch_32,
  idx_data_pitch_32p,
  idx_data_pitch_64,
  idx_data_pitch_left,
  idx_data_pitch_left_16p,
  idx_data_pitch_left_32,
  idx_data_pitch_left_32p,
  idx_data_pitch_left_64,
  idx_data_pitch_right,
  idx_data_pitch_right_16p,
  idx_data_pitch_right_32,
  idx_data_pitch_right_32p,
  idx_data_pitch_right_64,
  idx_data_span_measurement,
  idx_data_span_measurement_16p,
  idx_data_span_measurement_32,
  idx_data_span_measurement_32p,
  idx_data_span_measurement_64,
} from "../db/datafixtures/datafixtures";
import {
  GRIP_CONVENTIONAL,
  GRIP_FINGERTIP,
  GRIP_WARNINGS,
  profileIsComplete,
  stepCoach,
  stepFields,
  tr,
  visibleSteps,
} from "./wizardSteps";
import {
  conventionalRingSpan,
  cutToCutSpan,
  insertWidth,
  fingerPitchFromFlex,
  FINGER_FLEX_TABLE,
  insertCompensation,
  ringSpanFromMeasuredX,
  shiftThumbPitch,
  THUMB_ADJUSTMENTS,
  thumbAdjustmentTotal,
  withCompensation,
} from "./gripRules";
import {
  ApplyButton,
  CheckRow,
  Choice,
  ChoiceRow,
  FieldTile,
  SuggestionCard,
  SuggestionText,
  TileGrid,
} from "./WizardField";

/**
 * Measuring, in the order it is taught.
 *
 * The wizard owns no values of its own. Every tile opens the sheet's own
 * picker and every write goes through specsheetHandleForm, so leaving half
 * way through loses nothing -- the sheet behind it is already saved.
 *
 * What it adds is sequence, the technique text that used to live only on the
 * shop's aide-mémoire, and the three calculations in gripRules.
 */

// The wizard sits below the pickers it opens: bootstrap gives a modal 1055
// and its backdrop 1050, and a picker opened from here has to land on top.
const WizardLayer = createGlobalStyle`
  .wizard-backdrop { z-index: 1045; }
`;

const StyledModal = styled(Modal)`
  .modal-content {
    border: none;
    border-radius: ${(p) => p.theme.radius.lg};
    box-shadow: inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }

  .modal-header {
    border-bottom: none;
    padding: calc(${(p) => p.theme.spacing.md} + 4px)
      ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.sm};
  }

  .modal-body {
    padding: ${(p) => p.theme.spacing.md};
    background: #f5f6fa;
    display: flex;
    flex-direction: column;
    gap: ${(p) => p.theme.spacing.md};
    overflow: auto;
  }

  .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
    justify-content: space-between;
    gap: ${(p) => p.theme.spacing.sm};
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeLg};
  font-weight: ${(p) => p.theme.font.weightBold};
  color: ${(p) => p.theme.color.primary};
  line-height: 1.2;
`;

const StepCount = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.textMuted};
  font-variant-numeric: tabular-nums;
`;

const Progress = styled.div`
  display: flex;
  gap: 3px;
  padding: 0 ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.sm};
`;

const Segment = styled.button`
  flex: 1 1 0;
  height: 6px;
  min-width: 0;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: ${(p) =>
    p.$state === "current"
      ? p.theme.color.accent
      : p.$state === "done"
        ? p.theme.color.primary
        : p.$state === "skipped"
          ? p.theme.color.warning
          : p.theme.color.border};
`;

const Coach = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.color.surfaceAlt};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: ${(p) => p.theme.color.primary};
  }
`;

const Warning = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${(p) => p.theme.radius.md};
  border: 1px solid
    ${(p) => (p.$stop ? p.theme.color.error : p.theme.color.warning)};
  background: ${(p) =>
    p.$stop ? p.theme.color.errorBg : p.theme.color.warningBg};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightMedium};
  color: ${(p) => p.theme.color.text};

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: ${(p) => (p.$stop ? p.theme.color.error : p.theme.color.warning)};
  }
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing.sm};
`;

const BlockLabel = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: 11px;
  font-weight: ${(p) => p.theme.font.weightSemibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme.color.textMuted};
`;

const Summary = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${(p) => p.theme.color.border};
  border-radius: ${(p) => p.theme.radius.md};
  background: ${(p) => p.theme.color.surface};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};
  text-align: left;

  span:last-child {
    color: ${(p) => p.theme.color.textMuted};
    font-size: ${(p) => p.theme.font.sizeSm};
    white-space: nowrap;
  }
`;

const Done = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: ${(p) => p.theme.radius.md};
  border: 1px solid ${(p) => p.theme.color.success};
  background: ${(p) => p.theme.color.successBg};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};

  svg {
    color: ${(p) => p.theme.color.success};
  }
`;

const FootButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 8px 18px;
  border-radius: ${(p) => p.theme.radius.md};
  border: 1px solid
    ${(p) => (p.$primary ? p.theme.color.primary : p.theme.color.border)};
  background: ${(p) =>
    p.$primary ? p.theme.color.primary : p.theme.color.surface};
  color: ${(p) => (p.$primary ? "#ffffff" : p.theme.color.primary)};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightSemibold};

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$primary ? p.theme.color.primaryDark : "rgba(0, 24, 126, 0.08)"};
  }

  &:disabled {
    opacity: 0.45;
  }
`;

const ChoiceScroll = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spacing.sm};
  max-height: 168px;
  overflow-y: auto;
`;

const FootRight = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.sm};
`;

const NO_INSERTS = "No inserts";

// Which finger a card is talking about, for the ones that carry both.
const FINGER_NAMES = {
  f1: { key: "wizard.label.middle_finger", text: "Middle" },
  f2: { key: "wizard.label.ring_finger", text: "Ring" },
};

const WizardModalComponent = (props) => {
  const {
    show,
    onHide,
    specsheet,
    bowler,
    is2Hander,
    isReadOnly,
    requireEmail,
    requirePhone,
    spanMeasurement,
    spanRound,
    pitchMeasurement,
    spanDataset,
    pitchDataset,
    pitchLeftDataset,
    pitchRightDataset,
    specsheetHandleForm,
  } = props;
  const { t } = useTranslation();

  const [stepIndex, setStepIndex] = useState(0);
  const [skipped, setSkipped] = useState({});
  const [checked, setChecked] = useState({});
  const [pickerField, setPickerField] = useState("");
  const [measuredX, setMeasuredX] = useState("");
  const [fingerFlex, setFingerFlex] = useState("");
  const [thumbAdjust, setThumbAdjust] = useState({});
  const [sheetId, setSheetId] = useState(null);

  const sheet = specsheet || {};
  const grip = sheet.ball_grip || GRIP_CONVENTIONAL;

  // A sheet swapped underneath is a different bowler or a different session:
  // the answers so far do not belong to it. Reopening the same sheet keeps
  // its place, which is what makes "step out and use Calculate Oval" work.
  useEffect(() => {
    if (!show || !sheet.id || sheet.id === sheetId) return;

    setSheetId(sheet.id);
    setStepIndex(0);
    setSkipped({});
    setChecked({});
    setMeasuredX("");
    setFingerFlex("");
    setThumbAdjust({});
  }, [show, sheet.id, sheetId]);

  const steps = useMemo(
    () =>
      visibleSteps({
        grip,
        is2Hander,
        hasFingerInserts: !!sheet.f1_grip_type && sheet.f1_grip_type !== NO_INSERTS,
        needsIdentity: !profileIsComplete(bowler, {
          requireEmail,
          requirePhone,
        }),
      }),
    [grip, is2Hander, sheet.f1_grip_type, bowler, requireEmail, requirePhone],
  );

  const index = Math.min(stepIndex, steps.length - 1);
  const step = steps[index];

  const write = (values) => {
    if (isReadOnly) return;
    specsheetHandleForm({ specsheetValues: values });
  };

  const openPicker = (name) => {
    if (isReadOnly || !PICKABLE_FIELDS.includes(name)) return;
    setPickerField(name);
  };

  const displayValue = (name) => {
    const raw = sheet[name];
    if (!raw) return "";
    if (name === "ball_grip" || name === "hand") return t(raw);

    return raw;
  };

  const missingOf = (entry) =>
    stepFields(entry, { is2Hander }).filter(
      (item) => !item.auto && !sheet[item.name],
    );

  const segmentState = (entry, position) => {
    if (position === index) return "current";
    if (skipped[entry.id]) return "skipped";
    if (entry.kind === "review") return "todo";

    return missingOf(entry).length === 0 ? "done" : "todo";
  };

  /* ---------------- the calculations, offered ---------------- */

  const ringSuggestion = useMemo(() => {
    if (!step || step.kind !== "ring" || !sheet.f1_span_full) return null;

    if (grip === GRIP_CONVENTIONAL) {
      return {
        span: conventionalRingSpan(
          sheet.f1_span_full,
          spanMeasurement,
          spanDataset,
        ),
        differenceLabel: "+1/8",
      };
    }

    if (!measuredX) return null;

    return ringSpanFromMeasuredX(
      sheet.f1_span_full,
      measuredX,
      spanMeasurement,
      spanDataset,
    );
  }, [step, grip, sheet.f1_span_full, measuredX, spanMeasurement, spanDataset]);

  /**
   * Cut to cut is not measured, it is the full span less the insert -- so it
   * can only be offered once the insert and its size are known. The sheet's
   * own auto cut to cut does the same arithmetic, but only for shops that
   * turned that suggestion on, and only as a side effect of changing the
   * span. Here it is offered on its own, and says what it is waiting for.
   */
  const cutFor = (finger) => {
    const spanFull = sheet[`${finger}_span_full`];
    if (!spanFull) return null;

    const gripType = sheet[`${finger}_grip_type`];
    const noInserts = !gripType || gripType === NO_INSERTS;

    // A fingertip grip almost always ends up with inserts, and applying a cut
    // measured without one would go stale the moment they are chosen.
    if (grip === GRIP_FINGERTIP && noInserts) return { waiting: "inserts" };

    const width = insertWidth(
      gripType,
      sheet[`${finger}_size`],
      idx_data_insert_types,
      idx_data_insert_sizes,
      spanMeasurement,
      spanRound,
    );

    if (width === null) return { waiting: "size" };

    const span = cutToCutSpan(spanFull, width, spanMeasurement, spanDataset);

    return span ? { span, field: `${finger}_span_cut` } : null;
  };

  /**
   * What the sheet calls the "5/16 rule difference": the gap measured between
   * the two marks. Same list the sheet's own ring finger picker offers -- the
   * shop's pitch dataset up to a half -- so the wizard is not asking for the
   * same measurement in a vocabulary of its own. Offered as taps rather than
   * a dropdown because this is measured standing up, on a tablet.
   */
  const differenceOptions = useMemo(
    () =>
      (pitchDataset || [])
        .filter((entry) => entry.fraction !== "---" && entry.decimal <= 1 / 2)
        .map((entry) => entry.fraction),
    [pitchDataset],
  );

  const flexSuggestion = useMemo(
    () =>
      fingerFlex
        ? fingerPitchFromFlex(fingerFlex, pitchMeasurement, pitchDataset)
        : null,
    [fingerFlex, pitchMeasurement, pitchDataset],
  );

  const compensation = useMemo(
    () =>
      sheet.f1_grip_type && sheet.f1_grip_type !== NO_INSERTS
        ? insertCompensation(sheet.f1_grip_type, idx_data_insert_types)
        : null,
    [sheet.f1_grip_type],
  );

  const compensated = useMemo(() => {
    if (!compensation || !compensation.amount) return null;

    return {
      f1_lat: withCompensation(
        sheet.f1_lat,
        compensation.amount,
        pitchMeasurement,
        pitchLeftDataset,
      ),
      f2_lat: withCompensation(
        sheet.f2_lat,
        compensation.amount,
        pitchMeasurement,
        pitchRightDataset,
      ),
    };
  }, [
    compensation,
    sheet.f1_lat,
    sheet.f2_lat,
    pitchMeasurement,
    pitchLeftDataset,
    pitchRightDataset,
  ]);

  const thumbTotal = thumbAdjustmentTotal(thumbAdjust);
  const thumbAdjusted = useMemo(
    () =>
      thumbTotal
        ? shiftThumbPitch(
            { thm_for: sheet.thm_for, thm_rev: sheet.thm_rev },
            thumbTotal,
            pitchMeasurement,
            pitchDataset,
          )
        : null,
    [thumbTotal, sheet.thm_for, sheet.thm_rev, pitchMeasurement, pitchDataset],
  );

  /* ---------------- navigation ---------------- */

  const isLast = index === steps.length - 1;

  const goTo = (position) =>
    setStepIndex(Math.max(0, Math.min(position, steps.length - 1)));

  const next = () => {
    if (isLast) {
      onHide();
      return;
    }

    setSkipped((current) => R.dissoc(step.id, current));
    goTo(index + 1);
  };

  const skip = () => {
    setSkipped((current) => ({ ...current, [step.id]: true }));
    goTo(index + 1);
  };

  if (!show || !step) return null;

  /* ---------------- step bodies ---------------- */

  const renderChecks = () =>
    (step.checks || []).map((entry) => (
      <CheckRow key={entry.key}>
        <input
          type="checkbox"
          checked={!!checked[entry.key]}
          onChange={(event) =>
            setChecked((current) => ({
              ...current,
              [entry.key]: event.target.checked,
            }))
          }
        />
        <span>{tr(t, entry)}</span>
      </CheckRow>
    ));

  const renderIdentity = () => (
    <Block>
      <BlockLabel>{t("wizard.label.on_file", "On file")}</BlockLabel>
      <TileGrid>
        <FieldTile
          label={t("form.label.bowler", "Bowler")}
          value={`${bowler?.firstname || ""} ${bowler?.lastname || ""}`.trim()}
          disabled
        />
        <FieldTile
          label={t("form.placeholder.phone", "Phone")}
          value={bowler?.telephone || ""}
          disabled
        />
        <FieldTile
          label={t("form.label.customer_hand", "Customer hand")}
          value={bowler?.hand ? t(bowler.hand) : ""}
          disabled
        />
      </TileGrid>
      <Coach>
        <span>
          {t(
            "wizard.step.identity.edit",
            "Anything missing is edited with the pencil on the bowler's header.",
          )}
        </span>
      </Coach>
      {renderChecks()}
    </Block>
  );

  const renderCut = (fingers) => {
    const cuts = fingers
      .map((finger) => ({ finger, cut: cutFor(finger) }))
      .filter((entry) => entry.cut);

    if (cuts.length === 0) return null;

    const waiting = cuts.find((entry) => entry.cut.waiting);
    if (waiting) {
      return (
        <Coach>
          <Lightbulb />
          <span>
            {waiting.cut.waiting === "inserts"
              ? t(
                  "wizard.label.cut_waits_inserts",
                  "Cut to cut is the span less the insert, so it is offered once the insert is chosen.",
                )
              : t(
                  "wizard.label.cut_waits_size",
                  "Cut to cut is offered once the insert size is chosen.",
                )}
          </span>
        </Coach>
      );
    }

    const ready = cuts.filter((entry) => entry.cut.span);
    if (ready.length === 0) return null;

    const done = ready.every(
      (entry) => sheet[entry.cut.field] === entry.cut.span,
    );

    return (
      <SuggestionCard>
        <SuggestionText>
          <span>{t("wizard.label.suggested_cut", "Suggested cut to cut")}</span>
          <strong>
            {ready
              .map((entry) => `${tr(t, FINGER_NAMES[entry.finger])} ${entry.cut.span}`)
              .join(" · ")}
          </strong>
        </SuggestionText>
        <ApplyButton
          type="button"
          disabled={isReadOnly || done}
          onClick={() =>
            write(
              ready.reduce(
                (values, entry) => ({
                  ...values,
                  [entry.cut.field]: entry.cut.span,
                }),
                {},
              ),
            )
          }
        >
          {done
            ? t("wizard.action.applied", "Applied")
            : t("wizard.action.apply", "Apply")}
        </ApplyButton>
      </SuggestionCard>
    );
  };

  const renderRing = () => (
    <Block>
      {grip === GRIP_FINGERTIP && (
        <Block>
          <BlockLabel>
            {t("form.label.ring_finger_full_difference")}{" "}
            {t("wizard.label.measured_x", "— gap between the two marks")}
          </BlockLabel>
          <ChoiceScroll>
            {differenceOptions.map((option) => (
              <Choice
                key={option}
                type="button"
                $on={measuredX === option}
                onClick={() => setMeasuredX(option)}
              >
                {option}
              </Choice>
            ))}
          </ChoiceScroll>
        </Block>
      )}
      {renderCut(["f2"])}
      {ringSuggestion && ringSuggestion.span && (
        <SuggestionCard>
          <SuggestionText>
            <span>
              {t("wizard.label.suggested_ring", "Suggested ring finger span")} (
              {ringSuggestion.differenceLabel})
            </span>
            <strong>{ringSuggestion.span}</strong>
          </SuggestionText>
          <ApplyButton
            type="button"
            disabled={isReadOnly || sheet.f2_span_full === ringSuggestion.span}
            onClick={() => write({ f2_span_full: ringSuggestion.span })}
          >
            {sheet.f2_span_full === ringSuggestion.span
              ? t("wizard.action.applied", "Applied")
              : t("wizard.action.apply", "Apply")}
          </ApplyButton>
        </SuggestionCard>
      )}
    </Block>
  );

  const renderFingerFlex = () => (
    <Block>
      <BlockLabel>
        {t("wizard.label.first_joint", "First joint flexibility")}
      </BlockLabel>
      <ChoiceRow>
        {FINGER_FLEX_TABLE.map((row) => (
          <Choice
            key={row.degrees}
            type="button"
            $on={fingerFlex === row.degrees}
            onClick={() => setFingerFlex(row.degrees)}
          >
            {row.degrees}°
          </Choice>
        ))}
      </ChoiceRow>
      {flexSuggestion && (
        <SuggestionCard>
          <SuggestionText>
            <span>
              {flexSuggestion.zero
                ? t("wizard.label.zero_pitch", "Zero pitch with the right span")
                : `${t("wizard.label.reverse_range", "Reverse pitch")} ${
                    flexSuggestion.lowLabel
                  } – ${flexSuggestion.highLabel}`}
            </span>
            <strong>{flexSuggestion.reverse}</strong>
          </SuggestionText>
          <ApplyButton
            type="button"
            disabled={isReadOnly}
            onClick={() =>
              write({
                f1_rev: flexSuggestion.reverse,
                f2_rev: flexSuggestion.reverse,
                f1_for: "",
                f2_for: "",
              })
            }
          >
            {t("wizard.action.apply", "Apply")}
          </ApplyButton>
        </SuggestionCard>
      )}
    </Block>
  );

  const renderInserts = () => {
    if (!compensation) return null;

    return (
      <SuggestionCard>
        <SuggestionText>
          <span>
            {compensation.family} —{" "}
            {compensation.amount
              ? t("wizard.label.add_outer_angle", "add outer angle")
              : t("wizard.label.no_outer_angle", "no outer angle to add")}
          </span>
          <strong>
            {compensated
              ? `${sheet.f1_lat || "—"} → ${compensated.f1_lat} · ${
                  sheet.f2_lat || "—"
                } → ${compensated.f2_lat}`
              : t("wizard.label.laterals_stand", "Laterals stay as they are")}
          </strong>
        </SuggestionText>
        {compensated && (
          <ApplyButton
            type="button"
            disabled={
              isReadOnly ||
              (sheet.f1_lat === compensated.f1_lat &&
                sheet.f2_lat === compensated.f2_lat)
            }
            onClick={() => write(compensated)}
          >
            {t("wizard.action.apply", "Apply")}
          </ApplyButton>
        )}
      </SuggestionCard>
    );
  };

  const renderThumb = () => (
    <Block>
      <BlockLabel>
        {t("wizard.label.adjustments", "Adjustments")}
      </BlockLabel>
      <ChoiceRow>
        {THUMB_ADJUSTMENTS.map((adjustment) => (
          <Choice
            key={adjustment.id}
            type="button"
            $on={!!thumbAdjust[adjustment.id]}
            onClick={() =>
              setThumbAdjust((current) => ({
                ...current,
                [adjustment.id]: !current[adjustment.id],
              }))
            }
          >
            {t(
              `wizard.adjustment.${adjustment.id}`,
              adjustment.id.replace(/_/g, " "),
            )}
          </Choice>
        ))}
      </ChoiceRow>
      {thumbAdjusted && (
        <SuggestionCard>
          <SuggestionText>
            <span>{t("wizard.label.adjusted_pitch", "Adjusted thumb pitch")}</span>
            <strong>
              {thumbAdjusted.thm_for
                ? `${t("form.label.forward", "Forward")} ${thumbAdjusted.thm_for}`
                : `${t("form.label.reverse", "Reverse")} ${thumbAdjusted.thm_rev}`}
            </strong>
          </SuggestionText>
          <ApplyButton
            type="button"
            disabled={isReadOnly}
            onClick={() => write(thumbAdjusted)}
          >
            {t("wizard.action.apply", "Apply")}
          </ApplyButton>
        </SuggestionCard>
      )}
      {grip === GRIP_CONVENTIONAL && (sheet.thm_for || sheet.thm_rev) && (
        <SuggestionCard>
          <SuggestionText>
            <span>
              {t(
                "wizard.label.same_pitches",
                "Conventional: the same pitches at the fingers",
              )}
            </span>
            <strong>
              {sheet.thm_for
                ? `${t("form.label.forward", "Forward")} ${sheet.thm_for}`
                : `${t("form.label.reverse", "Reverse")} ${sheet.thm_rev}`}
            </strong>
          </SuggestionText>
          <ApplyButton
            type="button"
            disabled={isReadOnly}
            onClick={() =>
              write({
                f1_for: sheet.thm_for || "",
                f2_for: sheet.thm_for || "",
                f1_rev: sheet.thm_rev || "",
                f2_rev: sheet.thm_rev || "",
              })
            }
          >
            {t("wizard.action.copy", "Copy")}
          </ApplyButton>
        </SuggestionCard>
      )}
    </Block>
  );

  const renderReview = () => {
    const pending = steps
      .filter((entry) => entry.kind !== "review")
      .map((entry) => ({
        step: entry,
        missing: missingOf(entry),
      }))
      .filter((entry) => entry.missing.length > 0 || skipped[entry.step.id]);

    return (
      <Block>
        {pending.length === 0 ? (
          <Done>
            <Check2 />
            <span>
              {t("wizard.review.complete", "Every step has been answered.")}
            </span>
          </Done>
        ) : (
          pending.map(({ step: entry, missing }) => (
            <Summary
              key={entry.id}
              type="button"
              onClick={() =>
                goTo(steps.findIndex((item) => item.id === entry.id))
              }
            >
              <span>{tr(t, entry.title)}</span>
              <span>
                {skipped[entry.id]
                  ? t("wizard.review.skipped", "Skipped")
                  : missing.map((item) => tr(t, item.label)).join(" · ")}
              </span>
            </Summary>
          ))
        )}
        <Warning $stop={grip === GRIP_CONVENTIONAL}>
          <ExclamationTriangle />
          <span>{tr(t, GRIP_WARNINGS[grip])}</span>
        </Warning>
      </Block>
    );
  };

  const extras = {
    identity: renderIdentity,
    middle: () => renderCut(["f1"]),
    holes: () => renderCut(["f1", "f2"]),
    ring: renderRing,
    fingerFlex: renderFingerFlex,
    inserts: renderInserts,
    thumb: renderThumb,
    review: renderReview,
    grip: () => <Block>{renderChecks()}</Block>,
  };

  const coach = stepCoach(step, grip);
  const fields = stepFields(step, { is2Hander });

  return (
    <>
      <WizardLayer />
      <StyledModal
        show={show}
        onHide={onHide}
        centered
        size="lg"
        fullscreen="lg-down"
        backdrop="static"
        keyboard={false}
        backdropClassName="wizard-backdrop"
        style={{ zIndex: 1046 }}
      >
        <Modal.Header closeButton>
          <HeaderText>
            <Title>{tr(t, step.title)}</Title>
            <StepCount>
              {t("wizard.label.step", "Step")} {index + 1} / {steps.length}
              {" · "}
              {t(grip)}
            </StepCount>
          </HeaderText>
        </Modal.Header>
        <Progress>
          {steps.map((entry, position) => (
            <Segment
              key={entry.id}
              type="button"
              $state={segmentState(entry, position)}
              title={tr(t, entry.title)}
              aria-label={tr(t, entry.title)}
              onClick={() => goTo(position)}
            />
          ))}
        </Progress>
        <Modal.Body>
          {coach && (
            <Coach>
              <Lightbulb />
              <span>{tr(t, coach)}</span>
            </Coach>
          )}

          {extras[step.kind] && extras[step.kind]()}

          {fields.length > 0 && (
            <TileGrid>
              {fields.map((entry) => (
                <FieldTile
                  key={entry.name}
                  label={tr(t, entry.label)}
                  value={displayValue(entry.name)}
                  auto={entry.auto}
                  autoLabel={t("wizard.label.auto", "auto")}
                  disabled={isReadOnly}
                  onClick={() => openPicker(entry.name)}
                />
              ))}
            </TileGrid>
          )}
        </Modal.Body>
        <Modal.Footer>
          <FootButton
            type="button"
            disabled={index === 0}
            onClick={() => goTo(index - 1)}
          >
            <ChevronLeft /> {t("wizard.action.back", "Back")}
          </FootButton>
          <FootRight>
            {!isLast && (
              <FootButton type="button" onClick={skip}>
                {t("wizard.action.skip", "Skip")}
              </FootButton>
            )}
            <FootButton type="button" $primary onClick={next}>
              {isLast
                ? t("wizard.action.finish", "Finish")
                : t("wizard.action.next", "Next")}
              {!isLast && <ChevronRight />}
            </FootButton>
          </FootRight>
        </Modal.Footer>
      </StyledModal>

      <ModalSpecsheets
        openDropdown={pickerField}
        values={sheet}
        onCommit={(values) => write(values)}
        onClose={() => setPickerField("")}
      />
    </>
  );
};

export const WizardModal = connect(
  (state) => {
    const systemDefault = state.settings.appSettings.systemDefault;
    const spanMeasurement = systemDefault.span_measurement;
    const pitchMeasurement = systemDefault.pitch_measurement;

    return {
      specsheet: state.specsheet.currentSpecSheet,
      bowler: state.bowlers.selectedCustomer,
      isReadOnly: state.userSession.isReadOnly,
      requireEmail:
        state.settings.appSettings.bowlerPreferences?.require_email,
      requirePhone:
        state.settings.appSettings.bowlerPreferences?.require_phone,
      is2Hander:
        state.bowlers.selectedCustomer?.style === "two_handed" ||
        state.bowlers.selectedCustomer?.style === "thumbless",
      spanMeasurement,
      // The denominator the shop stores in, which is what the insert width has
      // to be rounded to before it can be taken off a span.
      spanRound: R.pathOr(16, [spanMeasurement])({
        16: 16,
        "16+": 32,
        32: 32,
        "32+": 64,
        64: 64,
        "64+": 128,
      }),
      pitchMeasurement,
      // The same datasets the pickers are built from: a value this wizard
      // proposes has to be one the picker can also show.
      spanDataset: R.pathOr(idx_data_span_measurement, [spanMeasurement])({
        32: idx_data_span_measurement_32,
        "16+": idx_data_span_measurement_16p,
        64: idx_data_span_measurement_64,
        "32+": idx_data_span_measurement_32p,
      }),
      pitchDataset: R.pathOr(idx_data_pitch, [pitchMeasurement])({
        32: idx_data_pitch_32,
        "16+": idx_data_pitch_16p,
        64: idx_data_pitch_64,
        "32+": idx_data_pitch_32p,
      }),
      pitchLeftDataset: R.pathOr(idx_data_pitch_left, [pitchMeasurement])({
        32: idx_data_pitch_left_32,
        "16+": idx_data_pitch_left_16p,
        64: idx_data_pitch_left_64,
        "32+": idx_data_pitch_left_32p,
      }),
      pitchRightDataset: R.pathOr(idx_data_pitch_right, [pitchMeasurement])({
        32: idx_data_pitch_right_32,
        "16+": idx_data_pitch_right_16p,
        64: idx_data_pitch_right_64,
        "32+": idx_data_pitch_right_32p,
      }),
    };
  },
  { specsheetHandleForm },
)(WizardModalComponent);

export default WizardModal;
